import { customElement, query } from 'lit/decorators.js';
import { WEB_COMPONENT_POLICY_PANEL_CONTENT_CONTROL_DATA_PROTECTION_RULES_GRID_LIGHT } from '../../../../../../../../../../webcomponents.constants';
import { GzPolicyUiSettingsGridWebComponent } from '../../../../../../../../../common/policyPanel/uiSettings/gridTemplate/gridComponents/gridView';
import {
  Column,
  ColumnKeys,
  FILTERS,
  getGridColumnDetails,
  GRID_COLUMNS,
  PREDEFINED_FILTERS_KEYS,
} from './grid-config';
import { GzContentControlDataProtectionRulesLightGridDataProvider } from './GzContentControlDataProtectionRulesLightGridDataProvider';
import { html, TemplateResult } from 'lit';
import { translateFn } from '../../../../../../../../../../utils/translator.helper';
import { ButtonColors, ButtonTypes } from '../../../../../../../../../../ui/gzButton/types';
import { GzButtonWebComponent } from '../../../../../../../../../../ui/gzButton';
import {
  ContentControlDataProtectionRuleLight,
  ContentControlDataProtectionRuleLightStatus,
} from '../../../../../../../utils/uiSettings/contentControl/content-control.types';

@customElement(WEB_COMPONENT_POLICY_PANEL_CONTENT_CONTROL_DATA_PROTECTION_RULES_GRID_LIGHT)
export class GzContentControlDataProtectionRulesLightGridWebComponent extends GzPolicyUiSettingsGridWebComponent<
  Column,
  ColumnKeys,
  ContentControlDataProtectionRuleLight,
  ContentControlDataProtectionRuleLight,
  GzContentControlDataProtectionRulesLightGridDataProvider
> {
  protected allColumnDetails = getGridColumnDetails();
  protected columns = GRID_COLUMNS;
  protected showHeaderCustomActionsInsideGrid = true;

  protected hasFilters = true;
  protected filters = FILTERS;
  protected predefinedFiltersKeys = PREDEFINED_FILTERS_KEYS;

  protected hasSelectableRows = true;
  protected hasDeleteRowAction = false;
  protected hasEditRowAction = false;
  protected hasEnableInlineRow = true;
  protected hasDisableInlineRow = true;

  protected translations = {
    enableRow: translateFn('Enable'),
    disableRow: translateFn('Disable'),
    emptyState: translateFn('No rules'),
  };

  @query('#enable-button')
  protected enableButton: GzButtonWebComponent;
  @query('#disable-button')
  protected disableButton: GzButtonWebComponent;

  public set items(value: ContentControlDataProtectionRuleLight[]) {
    super.setFormValue(value);
  }

  protected getDataProvider(): GzContentControlDataProtectionRulesLightGridDataProvider {
    return new GzContentControlDataProtectionRulesLightGridDataProvider(this.initialGridItems);
  }

  protected renderHeaderButtons(): TemplateResult {
    return html`
      <gz-button
        id="enable-button"
        type="${ButtonTypes.OUTLINE}"
        @click="${this.changeRulesEnabledStateOfSelectedItems.bind(this, true)}"
        >${translateFn('Enable')}
      </gz-button>

      <gz-button
        id="disable-button"
        type="${ButtonTypes.QUIET}"
        color="${ButtonColors.SECONDARY}"
        disabled
        @click="${this.changeRulesEnabledStateOfSelectedItems.bind(this, false)}"
        >${translateFn('Disable')}
      </gz-button>
    `;
  }

  protected onSelectRows(event: CustomEvent) {
    super.onSelectRows(event);

    const isDisabled = !event.detail.selectionStoreData.length;
    this.enableButton.disabled = isDisabled;
    this.disableButton.disabled = isDisabled;
  }

  protected onEnableGridItem(event: CustomEvent) {
    this.changeRuleEnabledState(event.detail.rowData.item.defaultPositionInGrid, true);
    this.onGridItemsChange();
    this.gridComponent.controller?.handleGridItemsLoad();
  }

  protected onDisableGridItem(event: CustomEvent) {
    this.changeRuleEnabledState(event.detail.rowData.item.defaultPositionInGrid, false);
    this.onGridItemsChange();
    this.gridComponent.controller?.handleGridItemsLoad();
  }

  private changeRulesEnabledStateOfSelectedItems(enabled: boolean) {
    if (!this.hasSelectableRows) {
      return;
    }

    this.selectedItemsGridData.forEach(position => this.changeRuleEnabledState(position, enabled));
    this.onGridItemsChange();
    this.gridComponent.controller?.handleGridItemsLoad(true, true);
  }

  private changeRuleEnabledState(position: number, enabled: boolean): void {
    const dataProvider = this.gridComponent.dataProvider as GzContentControlDataProtectionRulesLightGridDataProvider;
    const item = dataProvider.items[position - 1];
    const newStatus = enabled
      ? ContentControlDataProtectionRuleLightStatus.ENABLED
      : ContentControlDataProtectionRuleLightStatus.DISABLED;

    // skip if item is already in the desired state
    if (item.status === newStatus) {
      return;
    }

    // update item
    dataProvider.updateItem(position, {
      ...item,
      status: newStatus,
    });
  }
}
